import React, { useState, useMemo } from 'react';
import {
    View, StyleSheet, Platform, TouchableOpacity, ScrollView,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../context/ThemeContext';
import { Typography } from './ui';
import { useI18n } from '../i18n';
import { useCalendar } from '../context/CalendarContext';
import { fontWeights } from '../styles/typographyTokens';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../styles/accentText';
// ─── Types ───────────────────────────────────────────────
export interface CalendarEvent {
    id: number | string;
    title: string;
    time: string;
    date: string; // YYYY-MM-DD
    color: string;
    icon: string;
    isLive?: boolean;
    isReminded?: boolean;
    masterName?: string;
    tradition?: string;
    location?: string;
    endTime?: string;
}

interface ScheduleCalendarProps {
    events: CalendarEvent[];
    onDayPress?: (date: string) => void;
    onEventPress?: (event: CalendarEvent) => void;
    onAddToCalendar?: (event: CalendarEvent) => void;
    onRemindMe?: (event: CalendarEvent) => void;
    accentColor?: string;
    title?: string;
    isDesktop?: boolean;
    hideDetailPanel?: boolean;
    /** Dates with user-added reminders (rendered as orange stripes). Auto-read from CalendarContext if not provided. */
    markedDates?: Set<string>;
}

// ─── Helpers ─────────────────────────────────────────────
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year: number, month: number) => {
    const d = new Date(year, month, 1).getDay();
    return d === 0 ? 6 : d - 1;
};
const toISO = (y: number, m: number, d: number) =>
    `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const isToday = (y: number, m: number, d: number) => {
    const now = new Date();
    return now.getFullYear() === y && now.getMonth() === m && now.getDate() === d;
};

// ─── Component ───────────────────────────────────────────
export const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
    events,
    onDayPress,
    onEventPress,
    onAddToCalendar,
    onRemindMe,
    accentColor = '#E98245',
    title = 'Calendar',
    isDesktop = false,
    hideDetailPanel = false,
    markedDates: markedDatesProp,
}) => {
    const { isDark, colors: tc } = useTheme();
    const { t } = useI18n(['home', 'common']);
    const { markedDates: contextMarkedDates } = useCalendar();
    const markedDates = markedDatesProp || contextMarkedDates;
    const now = new Date();
    const [currentYear, setCurrentYear] = useState(now.getFullYear());
    const [currentMonth, setCurrentMonth] = useState(now.getMonth());
    const [selectedDate, setSelectedDate] = useState<string | null>(toISO(now.getFullYear(), now.getMonth(), now.getDate()));

    const eventMap = useMemo(() => {
        const map: Record<string, CalendarEvent[]> = {};
        events.forEach(ev => {
            if (!map[ev.date]) map[ev.date] = [];
            map[ev.date].push(ev);
        });
        return map;
    }, [events]);

    const monthEventCount = useMemo(() => {
        let count = 0;
        for (let d = 1; d <= getDaysInMonth(currentYear, currentMonth); d++) {
            const dateStr = toISO(currentYear, currentMonth, d);
            if (eventMap[dateStr]) count += eventMap[dateStr].length;
        }
        return count;
    }, [eventMap, currentYear, currentMonth]);

    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

    const prevMonth = () => {
        if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
        else setCurrentMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
        else setCurrentMonth(m => m + 1);
    };

    const handleDayPress = (day: number) => {
        const dateStr = toISO(currentYear, currentMonth, day);
        setSelectedDate(dateStr);
        onDayPress?.(dateStr);
    };

    const selectedEvents = selectedDate ? (eventMap[selectedDate] || []) : [];

    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    const formatSelectedDate = (dateStr: string) => {
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    };

    // Subtle accent tints
    const accentTint = accentColor + '10';
    const accentBorder = accentColor + '25';
    const accentSoft = accentColor + '18';

    // ─── Calendar Grid Panel ─────────────────────────
    const CalendarGrid = (
        <View style={[
            cs.calendarPanel,
            {
                backgroundColor: isDark ? tc.surfaceElevated : '#FFFFFF',
                borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
            Platform.OS === 'web' && {
                boxShadow: isDark
                    ? '0 1px 2px rgba(0,0,0,0.4), 0 8px 32px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.04)'
                    : '0 1px 2px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.03)',
            } as any,
        ]}>
            {/* Month navigation */}
            <View style={cs.monthRow}>
                <TouchableOpacity
                    onPress={prevMonth}
                    style={[
                        cs.navBtn,
                        {
                            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                        },
                    ]}
                    activeOpacity={0.7}
                >
                    <Ionicons name="chevron-back" size={18} color={tc.textPrimary} />
                </TouchableOpacity>

                <View style={cs.monthCenter}>
                    <Typography variant="h4" style={[cs.monthLabel, { color: tc.textPrimary }]}>
                        {MONTH_NAMES[currentMonth]} {currentYear}
                    </Typography>
                    <View style={[cs.monthMeta, { backgroundColor: accentSoft }]}>
                        <Ionicons name="calendar-outline" size={12} color={accentColor} style={{ marginRight: 4 }} />
                        <Typography variant="caption" style={[cs.monthMetaText, { color: accentColor }]}>
                            {t('scheduleCalendar.eventsThisMonth', { count: monthEventCount })}
                        </Typography>
                    </View>
                </View>

                <TouchableOpacity
                    onPress={nextMonth}
                    style={[
                        cs.navBtn,
                        {
                            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                        },
                    ]}
                    activeOpacity={0.7}
                >
                    <Ionicons name="chevron-forward" size={18} color={tc.textPrimary} />
                </TouchableOpacity>
            </View>

            {/* Day names */}
            <View style={[cs.dayNamesRow, { borderBottomColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }]}>
                {DAY_NAMES.map(d => (
                    <View key={d} style={cs.dayNameCell}>
                        <Typography variant="overline" style={[cs.dayNameText, { color: tc.textMuted }]}>{d.toUpperCase()}</Typography>
                    </View>
                ))}
            </View>

            {/* Grid */}
            <View style={cs.grid}>
                {cells.map((day, idx) => {
                    if (day === null) return <View key={`empty-${idx}`} style={cs.cell} />;
                    const dateStr = toISO(currentYear, currentMonth, day);
                    const dayEvents = eventMap[dateStr] || [];
                    const hasEvents = dayEvents.length > 0;
                    const isTodayCell = isToday(currentYear, currentMonth, day);
                    const isSelected = selectedDate === dateStr;

                    return (
                        <TouchableOpacity
                            key={`day-${day}`}
                            style={[
                                cs.cell,
                                isTodayCell && !isSelected && [cs.todayCell, {
                                    backgroundColor: accentTint,
                                    borderColor: accentBorder,
                                }],
                                isSelected && [cs.selectedCell, {
                                    backgroundColor: accentColor,
                                }],
                                isSelected && Platform.OS === 'web' && {
                                    boxShadow: `0 4px 16px ${accentColor}40`,
                                } as any,
                            ]}
                            activeOpacity={0.7}
                            onPress={() => handleDayPress(day)}
                        >
                            <Typography
                                variant="body"
                                style={[
                                    cs.dayText,
                                    { color: tc.textPrimary },
                                    isTodayCell && !isSelected && { fontWeight: fontWeights.bold, color: accentColor } as any,
                                    isSelected && { color: '#fff', fontWeight: fontWeights.bold } as any,
                                ]}
                            >
                                {day}
                            </Typography>
                            {/* Orange stripe for user-added reminders */}
                            {markedDates.has(dateStr) && (
                                <View style={[
                                    cs.markedStripe,
                                    {
                                        backgroundColor: isSelected ? 'rgba(255,255,255,0.85)' : accentColor,
                                    },
                                    isSelected && Platform.OS === 'web' && {
                                        boxShadow: `0 0 6px rgba(255,255,255,0.4)`,
                                    } as any,
                                ]} />
                            )}
                            {hasEvents && !markedDates.has(dateStr) && (
                                <View style={cs.dotRow}>
                                    {dayEvents.slice(0, 3).map((ev, i) => (
                                        <View
                                            key={i}
                                            style={[
                                                cs.dot,
                                                {
                                                    backgroundColor: isSelected ? 'rgba(255,255,255,0.9)' : ev.color,
                                                },
                                                isSelected && Platform.OS === 'web' && {
                                                    boxShadow: `0 0 4px ${ev.color}80`,
                                                } as any,
                                            ]}
                                        />
                                    ))}
                                </View>
                            )}
                        </TouchableOpacity>
                    );
                })}
            </View>

            {/* Today button */}
            <View style={cs.calendarFooter}>
                <TouchableOpacity
                    style={[cs.todayBtn, { borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }]}
                    onPress={() => {
                        const n = new Date();
                        setCurrentYear(n.getFullYear());
                        setCurrentMonth(n.getMonth());
                        handleDayPress(n.getDate());
                    }}
                    activeOpacity={0.7}
                >
                    <Ionicons name="today-outline" size={14} color={accentColor} />
                    <Typography variant="caption" style={[cs.todayBtnText, { color: accentColor }]}>
                        Today
                    </Typography>
                </TouchableOpacity>
            </View>
        </View>
    );

    // ─── Day Detail Panel ────────────────────────────
    const DayDetailPanel = (
        <View style={[
            cs.detailPanel,
            {
                backgroundColor: isDark ? tc.surfaceElevated : '#FFFFFF',
                borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
            Platform.OS === 'web' && {
                boxShadow: isDark
                    ? '0 1px 2px rgba(0,0,0,0.4), 0 8px 32px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.04)'
                    : '0 1px 2px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.03)',
            } as any,
        ]}>
            {/* Detail header */}
            <View style={[cs.detailHeader, { borderBottomColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }]}>
                <View style={{ flex: 1 }}>
                    <Typography variant="overline" style={[cs.detailOverline, { color: accentColor }]}>
                        {title.toUpperCase()}
                    </Typography>
                    <Typography variant="h4" style={[cs.detailDate, { color: tc.textPrimary }]}>
                        {selectedDate ? formatSelectedDate(selectedDate) : t('scheduleCalendar.selectDate')}
                    </Typography>
                </View>
                <View style={[cs.eventCountBadge, { backgroundColor: accentSoft, borderColor: accentBorder }]}>
                    <Ionicons name="layers-outline" size={13} color={accentColor} style={{ marginRight: 4 }} />
                    <Typography variant="caption" style={[cs.eventCountText, { color: accentColor }]}>
                        {t('scheduleCalendar.eventCount', { count: selectedEvents.length })}
                    </Typography>
                </View>
            </View>

            {/* Events list */}
            <ScrollView
                style={cs.detailScroll}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingBottom: 20, paddingTop: 4 }}
            >
                {selectedEvents.length > 0 ? (
                    selectedEvents.map(ev => (
                        <TouchableOpacity
                            key={ev.id}
                            style={[
                                cs.eventRow,
                                {
                                    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.012)',
                                    borderColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                },
                            ]}
                            activeOpacity={0.85}
                            onPress={() => onEventPress?.(ev)}
                        >
                            {/* Accent stripe */}
                            <View style={[cs.eventAccent, { backgroundColor: ev.color }]}>
                                <View style={[cs.eventAccentGlow, { backgroundColor: ev.color }]} />
                            </View>

                            <View style={cs.eventBody}>
                                <View style={cs.eventTopRow}>
                                    <Typography variant="body" style={[cs.eventTitle, { color: tc.textPrimary }]} numberOfLines={1}>
                                        {ev.title}
                                    </Typography>
                                    {ev.isLive && (
                                        <View style={cs.liveBadge}>
                                            <View style={cs.liveDot} />
                                            <Typography variant="overline" style={cs.liveText}>LIVE</Typography>
                                        </View>
                                    )}
                                </View>
                                {ev.masterName && (
                                    <Typography variant="caption" style={{ color: tc.textMuted, fontSize: 12, marginTop: 3, fontWeight: '500' } as any}>
                                        {ev.masterName}
                                    </Typography>
                                )}
                                <View style={cs.eventMetaRow}>
                                    <View style={[cs.metaChip, { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)' }]}>
                                        <Ionicons name="time-outline" size={11} color={tc.textMuted} />
                                        <Typography variant="caption" style={[cs.metaChipText, { color: tc.textMuted }]}>
                                            {ev.time}{ev.endTime ? ` — ${ev.endTime}` : ''}
                                        </Typography>
                                    </View>
                                    {ev.location && (
                                        <View style={[cs.metaChip, { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)' }]}>
                                            <Ionicons name="location-outline" size={11} color={tc.textMuted} />
                                            <Typography variant="caption" style={[cs.metaChipText, { color: tc.textMuted }]} numberOfLines={1}>
                                                {ev.location}
                                            </Typography>
                                        </View>
                                    )}
                                </View>
                                {/* Action row */}
                                <View style={cs.eventActions}>
                                    <TouchableOpacity
                                        style={[cs.actionBtn, { borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }]}
                                        activeOpacity={0.7}
                                        onPress={(e) => { e.stopPropagation(); onAddToCalendar?.(ev); }}
                                    >
                                        <Ionicons name="calendar-outline" size={13} color={accentColor} />
                                        <Typography variant="caption" style={[cs.actionText, { color: accentColor }]}>{t('common:calendar.addToCal')}</Typography>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={[
                                            cs.actionBtn,
                                            { borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
                                            ev.isReminded && {
                                                backgroundColor: '#F45122',
                                                borderColor: '#F26A36',
                                                ...(Platform.OS === 'web' && {
                                                    backgroundImage: 'linear-gradient(100deg, #F45122 0%, #FF955D 100%)',
                                                    boxShadow: '0 7px 20px rgba(244,81,34,0.20)',
                                                } as any),
                                            },
                                        ]}
                                        activeOpacity={0.7}
                                        onPress={(e) => { e.stopPropagation(); onRemindMe?.(ev); }}
                                    >
                                        <Ionicons name={ev.isReminded ? "notifications" : "notifications-outline"} size={13} color={ev.isReminded ? ON_ACCENT_TEXT : accentColor} />
                                        <Typography variant="caption" style={[cs.actionText, { color: ev.isReminded ? ON_ACCENT_TEXT : accentColor }]}>
                                            {ev.isReminded ? t('scheduleCalendar.reminded') : t('scheduleCalendar.remindMe')}
                                        </Typography>
                                    </TouchableOpacity>
                                </View>
                            </View>
                            <Ionicons name="chevron-forward" size={16} color={tc.textMuted} style={{ alignSelf: 'center', opacity: 0.4 }} />
                        </TouchableOpacity>
                    ))
                ) : (
                    <View style={cs.emptyDay}>
                        <View style={[cs.emptyIcon, { backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : accentTint }]}>
                            <Ionicons name="leaf-outline" size={32} color={isDark ? tc.textMuted : accentColor} style={{ opacity: 0.6 }} />
                        </View>
                        <Typography variant="body" style={[cs.emptyTitle, { color: tc.textPrimary }]}>
                            {t('scheduleCalendar.noEvents')}
                        </Typography>
                        <Typography variant="caption" style={[cs.emptySubtitle, { color: tc.textMuted }]}>
                            {t('scheduleCalendar.noEventsHint')}
                        </Typography>
                    </View>
                )}
            </ScrollView>
        </View>
    );

    // ─── Render ──────────────────────────────────────
    if (hideDetailPanel) return CalendarGrid;

    if (isDesktop) {
        return (
            <View style={cs.splitContainer}>
                <View style={cs.splitLeft}>{CalendarGrid}</View>
                <View style={cs.splitRight}>{DayDetailPanel}</View>
            </View>
        );
    }

    return (
        <View style={cs.stackedContainer}>
            {CalendarGrid}
            <View style={{ height: 20 }} />
            {DayDetailPanel}
        </View>
    );
};

// ─── Styles ──────────────────────────────────────────────
const cs = StyleSheet.create({
    // ─── Layout ───
    splitContainer: {
        flexDirection: 'row',
        gap: 20,
    } as any,
    splitLeft: {
        flex: 5,
        minWidth: 380,
    },
    splitRight: {
        flex: 4,
        minWidth: 320,
    },
    stackedContainer: {
        // stacked for mobile
    },

    // ─── Calendar Panel ───
    calendarPanel: {
        borderRadius: 24,
        overflow: 'hidden',
        borderWidth: 1,
    } as any,
    monthRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: 12,
    } as any,
    monthCenter: {
        alignItems: 'center',
    } as any,
    navBtn: {
        width: 40,
        height: 40,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
        }),
    } as any,
    monthLabel: {
        fontWeight: fontWeights.bold,
        fontSize: 20,
        letterSpacing: -0.5,
    } as any,
    monthMeta: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 6,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 20,
    } as any,
    monthMetaText: {
        fontSize: 14,
        fontWeight: fontWeights.bold,
        letterSpacing: 0.3,
    } as any,
    dayNamesRow: {
        flexDirection: 'row',
        paddingHorizontal: 16,
        marginTop: 4,
        paddingBottom: 10,
        borderBottomWidth: 1,
        marginBottom: 4,
    } as any,
    dayNameCell: {
        flex: 1,
        alignItems: 'center',
        paddingVertical: 4,
    } as any,
    dayNameText: {
        fontSize: 13,
        fontWeight: fontWeights.bold,
        letterSpacing: 1.2,
    } as any,
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        paddingHorizontal: 12,
        paddingTop: 4,
        paddingBottom: 4,
    } as any,
    cell: {
        width: '14.2857%',
        aspectRatio: 1 / 0.72,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 12,
        position: 'relative',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
        }),
    } as any,
    todayCell: {
        borderWidth: 1.5,
        borderRadius: 12,
    },
    selectedCell: {
        borderRadius: 12,
    },
    dayText: {
        fontSize: 16,
        fontWeight: '500',
        letterSpacing: -0.2,
    } as any,
    dotRow: {
        flexDirection: 'row',
        gap: 3,
        marginTop: 2,
        position: 'absolute',
        bottom: 6,
    } as any,
    dot: {
        width: 4,
        height: 4,
        borderRadius: 2,
    },
    calendarFooter: {
        flexDirection: 'row',
        justifyContent: 'center',
        paddingVertical: 14,
        paddingHorizontal: 24,
    } as any,
    todayBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 999,
        borderWidth: 1,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        }),
    } as any,
    todayBtnText: {
        fontSize: 14,
        fontWeight: fontWeights.bold,
    } as any,

    // ─── Detail Panel ───
    detailPanel: {
        borderRadius: 24,
        overflow: 'hidden',
        minHeight: 380,
        borderWidth: 1,
    } as any,
    detailHeader: {
        paddingHorizontal: 28,
        paddingTop: 28,
        paddingBottom: 20,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottomWidth: 1,
    } as any,
    detailOverline: {
        fontSize: 13,
        fontWeight: fontWeights.bold,
        letterSpacing: 2.5,
        marginBottom: 8,
    } as any,
    detailDate: {
        fontWeight: fontWeights.bold,
        fontSize: 24,
        letterSpacing: -0.5,
    } as any,
    eventCountBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 10,
        borderWidth: 1,
    } as any,
    eventCountText: {
        fontWeight: fontWeights.bold,
        fontSize: 14,
        letterSpacing: 0.2,
    } as any,
    detailScroll: {
        flex: 1,
        paddingHorizontal: 20,
    },

    // ─── Event row ───
    eventRow: {
        flexDirection: 'row',
        borderRadius: 16,
        padding: 18,
        marginBottom: 10,
        gap: 14,
        borderWidth: 1,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
        }),
    } as any,
    eventAccent: {
        width: 4,
        borderRadius: 4,
        alignSelf: 'stretch',
        position: 'relative',
        overflow: 'hidden',
    } as any,
    eventAccentGlow: {
        position: 'absolute',
        top: '20%',
        left: 0,
        right: 0,
        height: '60%',
        borderRadius: 4,
        opacity: 0.5,
        ...(Platform.OS === 'web' && {
            filter: 'blur(4px)',
        }),
    } as any,
    eventBody: {
        flex: 1,
    },
    eventTopRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    } as any,
    eventTitle: {
        fontWeight: fontWeights.bold,
        fontSize: 18,
        letterSpacing: -0.2,
        flex: 1,
    } as any,
    eventMetaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 8,
        gap: 6,
        flexWrap: 'wrap',
    } as any,
    metaChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    } as any,
    metaChipText: {
        fontSize: 13,
        fontWeight: '600',
        letterSpacing: 0.1,
    } as any,
    metaDivider: {
        width: 1,
        height: 10,
        marginHorizontal: 8,
        opacity: 0.2,
    },
    eventActions: {
        flexDirection: 'row',
        gap: 8,
        marginTop: 14,
    } as any,
    actionBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 7,
        borderRadius: 10,
        borderWidth: 1,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
        }),
    } as any,
    actionText: {
        fontSize: 14,
        fontWeight: fontWeights.bold,
        letterSpacing: 0.3,
    } as any,

    // ─── Misc ───
    liveBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: 'rgba(232,93,4,0.1)',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
    } as any,
    liveDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: '#E98245',
    },
    liveText: {
        color: ACCENT_TEXT,
        fontSize: 12,
        fontWeight: fontWeights.bold,
        letterSpacing: 1.2,
    } as any,
    emptyDay: {
        alignItems: 'center',
        paddingVertical: 56,
        paddingHorizontal: 24,
    } as any,
    emptyIcon: {
        width: 80,
        height: 80,
        borderRadius: 24,
        alignItems: 'center',
        justifyContent: 'center',
    } as any,
    emptyTitle: {
        fontWeight: fontWeights.bold,
        marginTop: 20,
        fontSize: 18,
        letterSpacing: -0.2,
    } as any,
    emptySubtitle: {
        marginTop: 6,
        textAlign: 'center',
        opacity: 0.6,
        fontSize: 15,
    } as any,

    // ─── Marked Date Stripe ───
    markedStripe: {
        width: 20,
        height: 3,
        borderRadius: 1.5,
        marginTop: 4,
    },
});

export default ScheduleCalendar;
